import * as React from "react";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";
import { StatusBadge } from "./status-badge";
import { Button } from "./button";
import { Switch } from "./switch";
import { 
  MapPin, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Loader2,
  Mail,
  Phone,
  Package,
  Plus
} from "lucide-react";
import { Supplier } from "../../lib/api";

interface MobileSupplierCardProps {
  supplier: Supplier;
  onView: (id: string) => void;
  onEdit: (supplier: Supplier) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onAddProduct: (supplier: Supplier) => void;
  isDeleting?: boolean;
  isToggling?: boolean;
  className?: string;
}

const MobileSupplierCard = React.forwardRef<
  HTMLDivElement,
  MobileSupplierCardProps
>(({ 
  supplier, 
  onView, 
  onEdit, 
  onDelete, 
  onToggleActive, 
  onAddProduct,
  isDeleting = false, 
  isToggling = false,
  className,
  ...props 
}, ref) => {
  
  // Helper function to get product names
  const getProductNames = (supplier: Supplier) => {
    return supplier.products && supplier.products.length > 0 
      ? `${supplier.products.length} products`
      : "No products";
  };

  // Helper function to check if supplier is verified
  const isVerified = (supplier: Supplier) => {
    return supplier.verificationStatus === 'verified';
  };

  return (
    <Card
      ref={ref}
      className={cn(
        "mobile-supplier-card bg-card border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-all hover:border-primary/20 cursor-pointer",
        className
      )}
      onClick={() => onView(supplier._id)}
      {...props}
    >
      <CardContent className="p-0 space-y-4">
        {/* Header with business name and verification status */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate">{supplier.businessName}</h3>
            <p className="text-sm text-muted-foreground truncate">{supplier.ownerName}</p>
          </div>
          <div className="flex flex-col items-end space-y-2 ml-2">
            <StatusBadge status={isVerified(supplier) ? 'verified' : 'pending'} />
          </div>
        </div>

        {/* Contact information */}
        <div className="space-y-2">
          <div className="flex items-center text-sm text-muted-foreground">
            <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">{supplier.email}</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{supplier.mobile}</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {supplier.address?.city}, {supplier.address?.state}
            </span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <Package className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{getProductNames(supplier)}</span>
          </div>
        </div>

        {/* Status and actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center space-x-3">
            <StatusBadge status={supplier.isActive ? 'active' : 'inactive'} />
            <div className="flex items-center space-x-2">
              <Switch
                checked={supplier.isActive}
                onCheckedChange={(checked) => {
                  // Prevent event bubbling to card click
                  onToggleActive(supplier._id);
                }}
                disabled={isToggling}
                aria-label={`Toggle ${supplier.businessName} active status`}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-xs text-muted-foreground">
                {supplier.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          
          <div className="flex space-x-1">
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onAddProduct(supplier);
              }}
              aria-label={`Add product for ${supplier.businessName}`}
              title="Add Product"
            >
              <Plus className="h-4 w-4 text-green-600" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(supplier);
              }}
              aria-label={`Edit ${supplier.businessName}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(supplier._id);
              }}
              disabled={isDeleting}
              aria-label={`Delete ${supplier.businessName}`}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 text-red-500" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

MobileSupplierCard.displayName = "MobileSupplierCard";

export { MobileSupplierCard };